import { formatUSD, formatCompact } from "@/lib/api";
import { useI18n } from "@/i18n";
import { ArrowUp, ArrowDown } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

const ACCENTS = {
  BTC: "#EAB308",
  ETH: "#60A5FA",
  SOL: "#9945FF",
  HYPE: "#22D3EE",
};

export default function CoinPanel({ symbol, data, active, onClick }) {
  const { t } = useI18n();
  const acc = ACCENTS[symbol] || "#22C55E";
  const up = (data?.change_24h ?? 0) >= 0;
  const spark = (data?.sparkline || []).map((p, i) => ({ i, p }));

  return (
    <button
      data-testid={`coin-panel-${symbol.toLowerCase()}`}
      onClick={onClick}
      className={`box-border w-full h-[196px] lg:h-auto lg:min-h-[196px] lg:flex-1 min-w-0 text-left border rounded-sm p-4 transition-colors duration-200 relative overflow-hidden ${
        active
          ? "border-white/30 bg-white/[0.03]"
          : "border-border/60 hover:border-border hover:bg-white/[0.02]"
      }`}
      style={active ? { boxShadow: `inset 3px 0 0 0 ${acc}` } : {}}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          {data?.image ? (
            <img src={data.image} alt={symbol} className="w-6 h-6 rounded-full" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-zinc-800" />
          )}
          <div>
            <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-wider">{data?.name || symbol}</div>
            <div className="font-display text-sm font-bold">{symbol}</div>
          </div>
        </div>
        <div className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${up ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"} flex items-center gap-0.5`}>
          {up ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
          {data ? Math.abs(data.change_24h).toFixed(2) : "-"}%
        </div>
      </div>

      <div className="font-mono text-2xl text-white leading-tight" data-testid={`coin-price-${symbol.toLowerCase()}`}>
        {data ? formatUSD(data.price) : <span className="shimmer inline-block w-24 h-6 rounded" />}
      </div>

      <div className="mt-2 h-12 shrink-0">
        {spark.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={spark}>
              <defs>
                <linearGradient id={`g-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={acc} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={acc} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="p" stroke={acc} strokeWidth={1.5} fill={`url(#g-${symbol})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] font-mono">
        <div>
          <div className="text-zinc-500 uppercase">7D</div>
          <div className={data?.change_7d >= 0 ? "text-emerald-400" : "text-red-400"}>
            {data?.change_7d != null ? (data.change_7d >= 0 ? "+" : "") + data.change_7d.toFixed(2) + "%" : "-"}
          </div>
        </div>
        <div>
          <div className="text-zinc-500 uppercase">Vol 24h</div>
          <div className="text-zinc-200">{data ? formatCompact(data.volume_24h) : "-"}</div>
        </div>
      </div>
    </button>
  );
}
